import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { taskAssignsApi } from '../axios';
import { CurrentUser } from '../axios/currentUser';
import { groupsApi } from '../axios/groups';
import { mobileUsersApi } from '../axios/mobileUsers';
import { useUserDataStore } from '../newStore/userDataStore';
import { MobileUser } from '../types';
import { UserGroup } from '../types/group';
import type { FilterMetadataMobileUser } from '../types/mobileUser';

export interface FindingsFilterUser {
  id: number;
  name: string;
  inactive: boolean;
}

export function useUsersQuery() {
  return useQuery<FilterMetadataMobileUser[]>({
    queryKey: ['allUsers'],
    queryFn: async (): Promise<FilterMetadataMobileUser[]> => {
      const { mobile_users } = await taskAssignsApi.getLightFilterMetadata();
      return mobile_users?.map((user) => ({
        id: user[1],
        name: user[0],
      }));
    },
  });
}

function formatMobileUserDisplayName(user: { name: string; last_name?: string }) {
  const parts = [user.name, user.last_name].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0,
  );
  return parts.join(' ').trim() || user.name;
}

async function fetchAllDeactivatedMobileUsersForFilter(): Promise<MobileUser[]> {
  const allUsers: MobileUser[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const pageData = await mobileUsersApi.getDeactivatedMobileUsers({ page });
    allUsers.push(...pageData.users);
    totalPages = pageData.total_pages;
    page += 1;
  } while (page <= totalPages && totalPages > 0);

  return allUsers;
}

function groupListContainsCurrentUser(group: UserGroup, currentUser: CurrentUser): boolean {
  return (
    group.users?.some(
      (userInGroup) =>
        userInGroup.mobile_user_id === currentUser.id || userInGroup.email === currentUser.email,
    ) ?? false
  );
}

/** Mobile user IDs that belong to any of the groups (union). */
function unionMobileUserIdsFromGroups(groups: UserGroup[]): Set<number> {
  const ids = new Set<number>();
  groups.forEach((group) => {
    group.users?.forEach((userInGroup) => {
      ids.add(userInGroup.mobile_user_id);
    });
  });
  return ids;
}

/** Active + deactivated mobile users for Findings assignee/creator filters (with inactive flag). */
export function useFindingsFilterUsersQuery() {
  return useQuery({
    queryKey: ['findingsFilterUsers', 'activeAndInactive'],
    queryFn: async (): Promise<FindingsFilterUser[]> => {
      const [activatedUsers, deactivatedUsers] = await Promise.all([
        mobileUsersApi.getAllActivatedMobileUsers(),
        fetchAllDeactivatedMobileUsersForFilter(),
      ]);

      const activeById = new Map<number, FindingsFilterUser>();
      activatedUsers.forEach((user) => {
        activeById.set(user.id, {
          id: user.id,
          name: user.name?.trim() || user.email || String(user.id),
          inactive: false,
        });
      });

      deactivatedUsers.forEach((user) => {
        if (activeById.has(user.id)) {
          return;
        }
        activeById.set(user.id, {
          id: user.id,
          name: formatMobileUserDisplayName(user),
          inactive: true,
        });
      });

      return Array.from(activeById.values()).sort((a, b) => {
        if (a.inactive !== b.inactive) {
          return a.inactive ? 1 : -1;
        }
        return a.name.localeCompare(b.name);
      });
    },
  });
}

export function useUserGroupsQuery() {
  return useQuery({
    queryKey: ['userGroups'],
    queryFn: async () => {
      const { groups } = await groupsApi.getGroups();
      return groups;
    },
  });
}

/**
 * Same as {@link useFindingsFilterUsersQuery}, but when the current user belongs to at least one
 * group, only users in those groups (union across groups) are returned. If they are not in any
 * group, all users are returned (active + inactive).
 */
export function useFindingsFilterUsersScopedQuery() {
  const { data: allUsers, isLoading: usersLoading } = useFindingsFilterUsersQuery();
  const { data: groupsData, isLoading: groupsLoading } = useUserGroupsQuery();
  const currentUser = useUserDataStore((state) => state.currentUser);

  const scopedUsers = useMemo(() => {
    const list = allUsers ?? [];
    const groups = groupsData ?? [];
    if (!currentUser) {
      return list;
    }

    const groupsWithCurrentUser = groups.filter((group) =>
      groupListContainsCurrentUser(group, currentUser),
    );

    if (groupsWithCurrentUser.length === 0) {
      return list;
    }

    const allowedIds = unionMobileUserIdsFromGroups(groupsWithCurrentUser);
    return list.filter((user) => allowedIds.has(user.id));
  }, [allUsers, groupsData, currentUser]);

  return {
    data: scopedUsers,
    isLoading: usersLoading || groupsLoading,
  };
}
